import { Control, PageField, Panel, RelatedTableControl } from "../types/interfaces";

export function walkPanels(panels: Panel[], callback: (panel: Panel, path: number[]) => void, path: number[] = []) {
    panels.forEach((panel, index) => {
        const currentPath = [...path, index];
        callback(panel, currentPath);

        panel.controls.forEach(c => {
            if (c.field && "controls" in c.field) {
                walkPanels([c.field as Panel], callback, [...currentPath, c.column]);
            }
        });
    });
}

export function getNestingDepth(panel: Panel, allPanels: Panel[], currentDepth = 0): number {
    for (const ctrl of panel.controls) {
        if (ctrl.field && "type" in ctrl.field && ctrl.field.type === "PANEL") {
            const childPanel = allPanels.find(p => p.panelId === (ctrl.field as Panel).panelId);
            if (childPanel) {
                const childDepth = getNestingDepth(childPanel, allPanels, currentDepth + 1);
                if (childDepth > currentDepth) currentDepth = childDepth;
            }
        }
    }
    return currentDepth;
}

export function getPanelAtPath(panels: Panel[], path: number[]): Panel | null {
    let currentPanel: Panel | null = null;
    let currentControls: Array<{ id: string; field?: PageField | Control | Panel; column: number }> = [];

    // First panel is at the root
    const firstIndex = path[0];
    currentPanel = panels[firstIndex];
    if (!currentPanel) return null;
    currentControls = currentPanel.controls;

    // Walk into nested panel controls
    for (let i = 1; i < path.length; i++) {
        const index = path[i];
        const panelItem = currentControls.find(c => c.column === index)?.field;

        if (!panelItem || !("controls" in panelItem)) return null;

        currentPanel = panelItem as Panel;
        currentControls = currentPanel.controls;
    }

    return currentPanel;
}

export function getParentPanel(panels: Panel[], path: number[]): Panel | null {
    if (path.length <= 1) return null;
    return getPanelAtPath(panels, path.slice(0, -1));
}


export function isPanel(obj: any): obj is Panel {
    return obj && typeof obj === "object" && "controls" in obj && "panelId" in obj;
}


/**
* Deletes a panel at the specified path from the nested panel structure.
* @param panels The root panel array.
* @param path An array of indexes to locate the panel. (e.g. [0] or [1, 0])
* @returns A new panel structure with the panel removed.
*/
export function deletePanelAtPath(panels: Panel[], path: number[]): Panel[] {
    const cloned = JSON.parse(JSON.stringify(panels)) as Panel[];

    if (path.length === 1) {
        cloned.splice(path[0], 1);
        return cloned;
    }

    let current = cloned;
    for (let i = 0; i < path.length - 1; i++) {
        const panelIndex = path[i];
        const control = current[panelIndex].controls.find(c => c.field && "type" in c.field && c.field.type === "PANEL");

        if (!control || !control.field || !("controls" in control.field)) return panels;
        current = (control.field as Panel).controls as any;
    }

    current.splice(path[path.length - 1], 1);
    return cloned;
}

export function isRelatedTableControl(ctrl: any): ctrl is RelatedTableControl {
    return ctrl && typeof ctrl === 'object' && ctrl.type === "RELATED_TABLE";
}


const fileSignatures: { [prefix: string]: string } = {
    // Images
    "iVBOR": "image/png",
    "/9j/": "image/jpeg",
    "R0lGODdh": "image/gif",
    "R0lGODlh": "image/gif",
    "Qk0": "image/bmp",
    "SUkqA": "image/tiff",
    "UklGR": "image/webp",
  
    // Documents
    "JVBERi0": "application/pdf",
    "UEsDB": "application/zip", // Also DOCX/XLSX/PPTX
    "PD94bWwg": "application/xml",
  
    // Audio / Video
    "T2dnUw": "video/ogg",
    "SUQzBA": "audio/mp3",
    "AAAAGGZ0": "video/mp4",
    "RIFF": "audio/wav",
  
    // Text
    "DQpUZXN0aW5n": "text/plain", // "Testing"
    "TWFuIGlzIGRpc3Rpbmd1aXNoZWQ": "text/plain", // "Man is distinguished..."
  
    // Executables
    "f0VMRg": "application/x-executable", // ELF
    "TVqQZ": "application/x-msdownload", // EXE
  };
  

  export const getMimeType = (base64String: string): string => {
    if (!base64String) return "application/octet-stream";
  
    // Extract from data URI if present
    if (base64String.startsWith("data:")) {
      return base64String.split(";")[0].replace("data:", "");
    }
  
    const keys = Object.keys(fileSignatures);
  
    for (const key of keys) {
      if (base64String.startsWith(key)) {
        return fileSignatures[key];
      }
    }
  
    return "application/octet-stream"; // Default fallback
  };
  
  export const getMimeTypeFromExtension = (ext: string | undefined): string => {
    const map: Record<string, string> = {
      pdf: "application/pdf",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      bmp: "image/bmp",
      tif: "image/tiff",
      tiff: "image/tiff",
      txt: "text/plain",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      csv: "text/csv",
      zip: "application/zip",
      xml: "application/xml",
      json: "application/json",
      mp4: "video/mp4",
      mp3: "audio/mpeg",
      wav: "audio/wav",
      avi: "video/x-msvideo",
      mov: "video/quicktime"
    };
  
    return map[ext?.toLowerCase() || ""] || "application/octet-stream";
  };
  

  
  export const getBlobLabel = (mime: string): string => {
    if (!mime) return "📁 View File";
  
    const map: { [key: string]: string } = {
      // Images
      "image/jpeg": "🖼 View JPEG",
      "image/png": "🖼 View PNG",
      "image/gif": "🖼 View GIF",
      "image/bmp": "🖼 View BMP",
      "image/webp": "🖼 View WebP",
      "image/tiff": "🖼 View TIFF",
  
      // Documents
      "application/pdf": "📄 View PDF",
      "application/msword": "📄 View Word",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "📄 View DOCX",
      "application/vnd.ms-excel": "📊 View Excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "📊 View XLSX",
      "application/vnd.ms-powerpoint": "📽 View PPT",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation": "📽 View PPTX",
      "text/plain": "📄 View Text",
      "text/csv": "📑 View CSV",
      "application/xml": "🗂 View XML",
      "application/json": "🧾 View JSON",
  
      // Media
      "video/mp4": "🎞 View MP4",
      "video/ogg": "🎞 View OGG",
      "audio/mpeg": "🎧 Listen MP3",
      "audio/mp3": "🎧 Listen MP3",
      "audio/wav": "🎧 Listen WAV",
  
      // Archives
      "application/zip": "🗜 View ZIP",
      "application/x-7z-compressed": "🗜 View 7z",
      "application/x-rar-compressed": "🗜 View RAR",
  
      // Code
      "application/javascript": "💻 View JS",
      "text/html": "🌐 View HTML",
      "text/css": "🎨 View CSS",
    };
  
    // Try exact match
    if (map[mime]) return map[mime];
  
    // Try partial match
    const match = Object.entries(map).find(([key]) => mime.includes(key));
    if (match) return match[1];
  
    return "📁 View File"; // Fallback
  };
  
  